import debounce from 'debounce'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GoogleMap, InfoWindow, useLoadScript } from '@react-google-maps/api'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import styled, { createGlobalStyle } from 'styled-components'
import has from 'styled-is'

import {
  modules as modulesResource,
  valves as valvesResource,
  sensorValues as sensorValuesResource,
  search as searchResource,
} from '../../redux/actions/resource'
import {
  getAllModules,
  getCurrentProjectId,
  getMapSearch,
  getAllValves,
  getAllSensorValues,
} from '../../redux/reducers'

import Action from '../../components/Action'
import {
  ClosableSearch,
  SearchWrapper,
  CloseIcon,
} from '../../components/forms'
import { Spinner } from '../../components/forms/buttons'
import { Options } from '../../components/forms/Dropdown'
import Icon from '../../components/Icon'
import Toolbar from '../../components/Toolbar'

import ModuleMarker from './ModuleMarker'
import ModuleOverlay from './ModuleOverlay'
import ModuleMarkerLegend from './ModuleMarkerLegend'
import ModuleHoverPopup from './ModuleHoverPopup'
import { getModuleDetails } from './moduleDetails'
import { getDenseModuleIds } from './mapMarkerDensity'
import { MARKER_MODES, getMarkerDisplayMode } from './ModuleMarker'

const HOVER_POPUP_OFFSETS = {
  [MARKER_MODES.DOT]: -15,
  [MARKER_MODES.COMPACT]: -45,
  [MARKER_MODES.DETAILED]: -45,
}

const StyledMap = styled(({ className, ...props }) => (
  <GoogleMap
    options={{
      disableDefaultUI: true,
      mapTypeId: 'hybrid',
      styles: [
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }],
        },
      ],
    }}
    mapContainerClassName={className}
    {...props}
  />
))`
  width: 100%;
  height: 100%;
  transition: height 0.1s ease;
`

const InfoWindowStyles = createGlobalStyle`
  .gm-style-iw-chr,
  .gm-ui-hover-effect {
    display: none !important;
  }
`

const MapWrapper = styled.div`
  position: relative;
  margin: 68px -2rem 0;
  width: calc(100% + 4rem);
  // TODO: avoid magic number
  height: calc(100% - 40px);
  transition: height 0.1s ease;
  ${has('overlay')`
    height: 25%;
  `};
`

const hasLatLng = ({ lng, lat }) => lat && lng
const libraries = ['geometry', 'drawing', 'places']

const useQuery = () => new URLSearchParams(useLocation().search)

const Map = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.GOOGLE_API_KEY,
    libraries,
  })
  const selectedModuleId = useQuery().get('moduleId')
  const [map, setMap] = useState(null)
  const [currentZoom, setCurrentZoom] = useState(null)
  const [isSearching, setSearching] = useState(false)
  const [hoveredModuleId, setHoveredModuleId] = useState(null)
  const hoverCloseTimeout = useRef(null)
  const modulesRef = useRef([])

  const searchFieldRef = useRef(null)

  const searchResults = useSelector(getMapSearch)

  const projectId = useSelector(getCurrentProjectId)
  const modules = useSelector((state) => getAllModules(state, hasLatLng))
  modulesRef.current = modules
  const valves = useSelector(getAllValves)
  const sensorValues = useSelector(getAllSensorValues)
  const hoveredModule = modules.find(({ id }) => id === hoveredModuleId)
  const denseModuleIds = useMemo(
    () => getDenseModuleIds(modules, currentZoom),
    [modules, currentZoom]
  )
  const hoveredMarkerMode = hoveredModule
    ? getMarkerDisplayMode(hoveredModule, denseModuleIds, currentZoom)
    : null

  useEffect(() => {
    if (projectId != null) {
      dispatch(modulesResource.list({ projectId }))
      dispatch(valvesResource.list({ projectId }))
      dispatch(sensorValuesResource.list({ projectId }))
      setHoveredModuleId(null)
    }
  }, [dispatch, projectId])

  const refreshModule = (moduleId) => {
    dispatch(modulesResource.read(moduleId))
    dispatch(valvesResource.list({ projectId }))
    dispatch(sensorValuesResource.list({ projectId }))
  }

  const cancelHoverClose = () => {
    if (hoverCloseTimeout.current != null) {
      window.clearTimeout(hoverCloseTimeout.current)
      hoverCloseTimeout.current = null
    }
  }

  const hoverModule = (moduleId) => {
    cancelHoverClose()
    setHoveredModuleId(moduleId)
  }

  const closeHoveredModule = () => {
    cancelHoverClose()
    hoverCloseTimeout.current = window.setTimeout(
      () => setHoveredModuleId(null),
      150
    )
  }

  useEffect(
    () => () => {
      cancelHoverClose()
    },
    []
  )

  const onLoad = useCallback((map) => {
    setMap(map)
    setCurrentZoom(map.getZoom?.() ?? null)
  }, [])
  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  useEffect(() => {
    if (isSearching) {
      dispatch(searchResource.read('map', { projectId }))
      searchFieldRef?.current?.focus()
    }
  }, [dispatch, isSearching, projectId, searchFieldRef])

  const onSearch = (search) => {
    dispatch(searchResource.read('map', { projectId, search }))
  }
  const [searchParams, updateSearchParams] = useState('')
  const debouncedSearch = useRef(debounce(onSearch, 1000)).current
  const throttleSearch = (e) => {
    const inputValue = e.target.value
    updateSearchParams(inputValue)
    const params = new window.URLSearchParams()
    if (inputValue) {
      params.append('search_keyword', inputValue)
    }
    debouncedSearch(params)
  }

  const moduleIdSum = modules.reduce((sum, { id }) => sum + id, 0)
  useEffect(() => {
    if (map != null) {
      const bounds = modulesRef.current.reduce(
        (bounds, { lat, lng }) => bounds.extend({ lat, lng }),
        new window.google.maps.LatLngBounds()
      )
      map.fitBounds(bounds)
      map.setCenter(bounds.getCenter())
    }
    // module array changes too often, projectId and sum of moduleIds should be
    // a safe indicator for changed project and loaded modules.
  }, [map, projectId, moduleIdSum])

  const renderMap = () => {
    // wrapping to a function is useful in case you want to access `window.google`
    // to eg. setup options or create latLng object, it won't be available otherwise
    // feel free to render directly if you don't need that
    return (
      <StyledMap
        onLoad={onLoad}
        onUnmount={onUnmount}
        onZoomChanged={() => setCurrentZoom(map?.getZoom?.() ?? null)}
      >
        {(modules || []).map((module, index) => {
          return (
            <ModuleMarker
              key={index}
              onClick={() => {
                cancelHoverClose()
                setHoveredModuleId(null)
                history.push(`/map?moduleId=${module.id}`)
              }}
              onMouseOver={() => hoverModule(module.id)}
              onMouseOut={closeHoveredModule}
              selected={module.id === +selectedModuleId}
              module={module}
              displayMode={getMarkerDisplayMode(
                module,
                denseModuleIds,
                currentZoom
              )}
            />
          )
        })}
        {hoveredModule && selectedModuleId == null && (
          <InfoWindow
            options={{
              pixelOffset: new window.google.maps.Size(
                0,
                HOVER_POPUP_OFFSETS[hoveredMarkerMode]
              ),
            }}
            position={{ lat: hoveredModule.lat, lng: hoveredModule.lng }}
            onCloseClick={() => setHoveredModuleId(null)}
          >
            <div onMouseOver={cancelHoverClose} onMouseOut={closeHoveredModule}>
              <ModuleHoverPopup
                module={hoveredModule}
                {...getModuleDetails(hoveredModule.id, valves, sensorValues)}
                onRefresh={() => refreshModule(hoveredModule.id)}
              />
            </div>
          </InfoWindow>
        )}
      </StyledMap>
    )
  }

  return (
    <>
      <Toolbar
        onBack={() => history.push('/dashboard')}
        search={
          isSearching ? (
            <SearchWrapper>
              <CloseIcon
                onClick={() => {
                  updateSearchParams('')
                  setSearching(false)
                }}
              />
              <ClosableSearch
                ref={searchFieldRef}
                value={searchParams}
                onChange={throttleSearch}
              />
              <Options open>
                {(searchResults || []).map(
                  (
                    { id, name, mapDisplayName, type, wateringModuleId },
                    index
                  ) => (
                    <li key={index}>
                      <a
                        onClick={() => {
                          history.push(
                            `/map?moduleId=${
                              ['Valve'].includes(type) ? wateringModuleId : id
                            }`
                          )
                          updateSearchParams('')
                          setSearching(false)
                        }}
                      >
                        {mapDisplayName || name}
                      </a>
                    </li>
                  )
                )}
              </Options>
            </SearchWrapper>
          ) : null
        }
      >
        <Action onClick={() => setSearching(true)}>
          <Icon name="search" />
        </Action>
      </Toolbar>
      {isLoaded ? (
        <MapWrapper overlay={selectedModuleId != null}>
          <InfoWindowStyles />
          {renderMap()}
          <ModuleMarkerLegend />
        </MapWrapper>
      ) : (
        <Spinner />
      )}
      {selectedModuleId != null && (
        <ModuleOverlay
          moduleId={+selectedModuleId}
          onClose={() => history.push('/map')}
        />
      )}
    </>
  )
}

export default Map
